import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Paper,
  Tooltip,
} from '@mui/material';
import type { BomResult } from '@/types';
import { formatNumber, sortSkillsByProgression } from '@/utils';
import { formatISK } from '@/utils/formatters';

interface ResultsTablesProps {
  result: BomResult;
}

export const ResultsTables: React.FC<ResultsTablesProps> = ({ result }) => {
  // Calculate material savings for highlighting (but not displaying in separate column)
  const materialSavings = result.rawMaterials.map((mat) => ({
    ...mat,
    saved: mat.quantity - mat.adjustedQuantity,
    percentSaved: ((mat.quantity - mat.adjustedQuantity) / mat.quantity) * 100,
  }));

  // Sort skills by progression: Base -> Advanced -> Expert, grouped by base skill
  const sortedSkillDetails = React.useMemo(() => {
    if (!result.skillDetails || result.skillDetails.length === 0) {
      return [];
    }
    const skillNames = result.skillDetails.map((s) => s.name);
    const sortedNames = sortSkillsByProgression(skillNames);
    return sortedNames
      .map((name) => result.skillDetails!.find((s) => s.name === name))
      .filter((s): s is NonNullable<typeof s> => s !== undefined);
  }, [result.skillDetails]);

  return (
    <Stack spacing={3}>
      {/* Summary Card */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">Calculation Summary</Typography>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Target Item
              </Typography>
              <Typography variant="h5">{result.targetItem}</Typography>
              {result.targetItemPrice && (
                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'medium' }}>
                  Market Price: {formatISK(result.targetItemPrice)}
                </Typography>
              )}
              {result.targetItemManufactureCost && (
                <Typography variant="body2" color="secondary.main" sx={{ fontWeight: 'medium' }}>
                  Manufacturing Cost: {formatISK(result.targetItemManufactureCost)}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Quantity: {formatNumber(result.targetQuantity)}
              </Typography>
              {result.finalMaterialEfficiency !== undefined && (
                <Typography variant="body2" color="info.main" sx={{ mt: 1, fontWeight: 'medium' }}>
                  Material Efficiency: {result.finalMaterialEfficiency.toFixed(1)}%
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip
                label={`${result.rawMaterials.length} Raw Materials`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`${result.intermediateItems.length} Intermediate Items`}
                color="secondary"
                variant="outlined"
              />
              {result.skillsApplied.length > 0 && (
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Skills Applied:
                      </Typography>
                      {sortedSkillDetails.map((skill) => (
                        <Typography key={skill.name} variant="body2" sx={{ mb: 0.5 }}>
                          • {skill.name} L{skill.level}: {skill.efficiencyPercent}% reduction
                        </Typography>
                      ))}
                      {sortedSkillDetails.length > 0 && (
                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                          Total:{' '}
                          {sortedSkillDetails.reduce((sum, s) => sum + s.efficiencyPercent, 0)}%
                          reduction
                        </Typography>
                      )}
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <Chip
                    label={`${result.skillsApplied.length} Skills Applied`}
                    color="success"
                    variant="outlined"
                    sx={{ cursor: 'help' }}
                  />
                </Tooltip>
              )}
            </Stack>

            <Typography variant="caption" color="text.secondary">
              Calculated in {result.calculationTime}ms
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* Raw Materials Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Raw Materials Required
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Total: {formatNumber(result.totalRawMaterialCost)} units
            {result.totalMarketCost && <> • Market Cost: {formatISK(result.totalMarketCost)}</>}
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Material</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Base Qty</TableCell>
                  <TableCell align="right">With Skills</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Total Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {materialSavings.map((mat) => (
                  <TableRow key={mat.itemId} hover>
                    <TableCell>
                      <Typography variant="body2">{mat.itemName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {mat.category}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{formatNumber(mat.quantity)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={mat.saved > 0 ? 'bold' : 'normal'}
                        color={mat.saved > 0 ? 'success.main' : 'text.primary'}
                      >
                        {formatNumber(mat.adjustedQuantity)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {mat.unitPrice !== undefined ? (
                        <Typography variant="body2" color="text.secondary">
                          {formatISK(mat.unitPrice)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {mat.totalPrice !== undefined ? (
                        <Typography variant="body2" fontWeight="medium">
                          {formatISK(mat.totalPrice)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total Row */}
                {result.totalMarketCost && (
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" fontWeight="bold">
                        Total Market Cost
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {/* Empty cell for Unit Price column */}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {formatISK(result.totalMarketCost)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Intermediate Items Table */}
      {result.intermediateItems.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Intermediate Items to Manufacture
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              These components must be manufactured first using the raw materials above.
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Component</TableCell>
                    <TableCell align="right">Quantity Needed</TableCell>
                    <TableCell align="right">Manufacturing Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.intermediateItems.map((item) => {
                    const manufacturingCost = item.blueprint.manufacture_cost * item.runs;
                    return (
                      <TableRow key={item.itemId} hover>
                        <TableCell>
                          <Typography variant="body2">{item.itemName}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{formatNumber(item.quantity)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatISK(manufacturingCost)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Total Row */}
                  {result.totalIntermediateManufactureCost && (
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell colSpan={2}>
                        <Typography variant="body2" fontWeight="bold">
                          Total Manufacturing Cost
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="secondary">
                          {formatISK(result.totalIntermediateManufactureCost)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};
